/**
 * Inferencia del país del usuario a partir del **browser** — ver ADR-2026-07-29-pais-browser-fallback-precios-cuando.
 *
 * Para qué: el catálogo de pricing necesita un país para resolver moneda y
 * precio (`checkoutPlans`), pero durante el alta (`/configuracion-cuenta/*`) el
 * usuario **todavía no tiene empresa**, y `company_profiles.pais` —la única
 * fuente de verdad del país en toda la app— recién se escribe en `/onboarding`
 * (`lib/createCompany.ts`). Sin país, `checkoutPlans` cae a USD, que para el
 * grueso del público (Argentina) es el precio equivocado.
 *
 * Cómo: timezone IANA primero (`Intl.DateTimeFormat().resolvedOptions().timeZone`),
 * que es el dato más confiable del browser y no depende del idioma de la UI, y
 * si no matchea, la región del `navigator.language` (`es-AR` → `AR`).
 *
 * ⚠️ Es una **heurística de display**: sirve para mostrar el precio probable en
 * una pantalla informativa. NO reemplaza al país declarado: cuando el usuario
 * carga su país en `/onboarding`, ese gana y es el que viaja al checkout. Nunca
 * se usa para cobrar.
 *
 * Devuelve `null` cuando no puede afirmar nada (país sin tabla propia, browser
 * sin `Intl`, timezone genérica): el fallback USD de `checkoutPlans` ya es la
 * respuesta correcta para todo lo no contemplado.
 */
import { paisDesdeCodigoISO } from './checkoutPlans';

/**
 * Timezones IANA → código ISO, sólo para los 5 países con tabla de precios
 * propia. Incluye los alias legacy (`America/Buenos_Aires`, `Mexico/*`,
 * `Chile/*`) porque algunos browsers/SO viejos todavía los reportan.
 */
const TZ_POR_PAIS: Record<string, string[]> = {
  AR: ['America/Buenos_Aires', 'America/Cordoba', 'America/Rosario', 'America/Mendoza'],
  CL: ['America/Santiago', 'America/Punta_Arenas', 'Pacific/Easter'],
  CO: ['America/Bogota'],
  MX: [
    'America/Mexico_City',
    'America/Cancun',
    'America/Merida',
    'America/Monterrey',
    'America/Matamoros',
    'America/Chihuahua',
    'America/Ciudad_Juarez',
    'America/Ojinaga',
    'America/Mazatlan',
    'America/Bahia_Banderas',
    'America/Hermosillo',
    'America/Tijuana',
  ],
  PE: ['America/Lima'],
};

/** Prefijos de timezone que identifican un país sin enumerar cada ciudad. */
const TZ_PREFIJO_POR_PAIS: Record<string, string[]> = {
  // `America/Argentina/Buenos_Aires`, `.../Cordoba`, `.../Ushuaia`, etc.
  AR: ['America/Argentina/'],
  CL: ['Chile/'],
  MX: ['Mexico/'],
};

/** Código ISO del país inferido por timezone, o `null`. */
function codigoPorTimezone(): string | null {
  let tz: string | undefined;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return null;
  }
  if (!tz) return null;
  for (const [codigo, zonas] of Object.entries(TZ_POR_PAIS)) {
    if (zonas.includes(tz)) return codigo;
  }
  for (const [codigo, prefijos] of Object.entries(TZ_PREFIJO_POR_PAIS)) {
    if (prefijos.some((p) => tz!.startsWith(p))) return codigo;
  }
  return null;
}

/**
 * Código ISO del país inferido por el idioma del browser, o `null`.
 *
 * Sólo sirve cuando el locale trae **región** (`es-AR`); un `es` pelado no dice
 * nada, y por eso no se asume Argentina: preferimos USD antes que un precio
 * inventado.
 */
function codigoPorIdioma(): string | null {
  const locales =
    typeof navigator === 'undefined'
      ? []
      : [...(navigator.languages ?? []), navigator.language].filter(Boolean);
  for (const locale of locales) {
    const region = locale!.split('-')[1];
    if (region && paisDesdeCodigoISO(region)) return region.toUpperCase();
  }
  return null;
}

/**
 * País probable del usuario según el browser, en el mismo formato que espera
 * `paisFlags` (`'Argentina'`, `'México'`, …). `null` si no se puede afirmar.
 */
export function paisDelBrowser(): string | null {
  const codigo = codigoPorTimezone() ?? codigoPorIdioma();
  return codigo ? paisDesdeCodigoISO(codigo) : null;
}

export default paisDelBrowser;
